/**
 * Trustpilot Metric + Dimension Catalogue (Story 23.3 + Story 23.4 + Story 23.8 + Story 35.3)
 *
 * Story 23.3 shipped 30 priority metrics + 12 dimensions. Story 23.4
 * added 3 synthetic-aggregate metrics. Story 23.8 added the derived
 * `unrepliedReviewCount`. **Story 35.3 adds 25 net-new metrics**
 * across 3 batches:
 *   - Batch 1 per-review private fields (+10)
 *   - Batch 2 Invitations API (+8 wire + 3 derived rates flagged synthetic)
 *   - Batch 3 paginated history lifetime aggregates (+4)
 *
 * Post-Story-35.3 totals: 34 + 25 = **59 MetricDefinitions** + 12
 * Dimensions. The Story 35.3 spec AC #8 references "+22 net-new" — the
 * 3-metric delta is the synthetic-flagged Invitations rates which the
 * spec counts INSIDE the Batch 2 +8 (verbatim "PLUS 3 synthetic derived
 * rates" in the Change Log). Pass 11 honest-recount invariant: 25 new
 * entries added, documented here for the next maintainer.
 *
 * **Build-time test** in `metric-allowlist.test.ts` asserts every
 * `apiField` value is present in `allTrustpilotFields()` from
 * `./fields`. Catches typos like `score.trustscore` → `score.trustScore`.
 */
import type { DimensionDefinition, MetricDefinition } from "../adapter";

export const TRUSTPILOT_METRICS: ReadonlyArray<MetricDefinition> = [
  // === Business Unit Scorecard Group (10) =================================
  {
    key: "displayName",
    label: "Business Unit Display Name",
    type: "number",
    dataType: "Enum",
    category: "reviews",
    compatibleWidgets: ["kpi", "data-table"],
    apiField: "displayName",
    description: "The Trustpilot display name of the business unit.",
  },
  {
    key: "companyName",
    label: "Company Name",
    type: "number",
    dataType: "Enum",
    category: "reviews",
    compatibleWidgets: ["data-table"],
    apiField: "companyName",
    description: "Legal company name as registered on Trustpilot.",
  },
  {
    key: "country",
    label: "Country",
    type: "number",
    dataType: "Enum",
    category: "reviews",
    compatibleWidgets: ["kpi", "data-table"],
    apiField: "address.countryCode",
    description: "ISO 3166-1 alpha-2 country code (e.g. 'US', 'DK').",
  },
  {
    key: "isSubscriber",
    label: "Is Trustpilot Subscriber",
    type: "number",
    dataType: "Enum",
    category: "reviews",
    compatibleWidgets: ["data-table"],
    apiField: "isSubscriber",
    description: "True when the business unit holds a paid Trustpilot subscription.",
  },
  {
    key: "isClaimed",
    label: "Is Business Claimed",
    type: "number",
    dataType: "Enum",
    category: "reviews",
    compatibleWidgets: ["data-table"],
    apiField: "isClaimed",
    description: "True when the business unit has been claimed by its owner on Trustpilot.",
  },
  {
    key: "isFacebookActivated",
    label: "Facebook Reviews Activated",
    type: "number",
    dataType: "Enum",
    category: "reviews",
    compatibleWidgets: ["data-table"],
    apiField: "isFacebookActivated",
    description: "True when the business has connected Facebook reviews to Trustpilot.",
  },
  {
    key: "isIncentivisingUsers",
    label: "Is Incentivising Reviewers",
    type: "number",
    dataType: "Enum",
    category: "reviews",
    compatibleWidgets: ["data-table"],
    apiField: "isIncentivisingUsers",
    description: "True when the business is known to offer incentives for reviews (transparency signal).",
  },
  {
    key: "facebookPageId",
    label: "Facebook Page ID",
    type: "number",
    dataType: "Enum",
    category: "reviews",
    compatibleWidgets: ["data-table"],
    apiField: "facebookPageId",
    description: "Connected Facebook page identifier (when activated).",
  },
  {
    key: "facebookPageUrl",
    label: "Facebook Page URL",
    type: "number",
    dataType: "Enum",
    category: "reviews",
    compatibleWidgets: ["data-table"],
    apiField: "facebookPageUrl",
    description: "Connected Facebook page URL (when activated).",
  },
  {
    key: "addressCity",
    label: "Address City",
    type: "number",
    dataType: "Enum",
    category: "reviews",
    compatibleWidgets: ["data-table"],
    apiField: "address.city",
    description: "Business unit registered city (from Trustpilot address record).",
  },

  // === TrustScore & Volume Group (5) ======================================
  {
    key: "trustScore",
    label: "TrustScore",
    type: "number",
    dataType: "Decimal",
    aggregationKind: "ratio",
    category: "trust-score",
    compatibleWidgets: ["kpi", "gauge", "line-chart"],
    apiField: "score.trustScore",
    description:
      "The headline 0-5 TrustScore (decimal — the true weighted average; gauge widget config max = 5.0).",
  },
  {
    key: "stars",
    label: "Star Rating",
    type: "number",
    dataType: "Integer",
    aggregationKind: "ratio", ratioComponents: { denominator: "totalReviews" },
    category: "trust-score",
    compatibleWidgets: ["kpi", "gauge"],
    apiField: "score.stars",
    description: "Rounded 1-5 integer star rating derived from the TrustScore.",
  },
  {
    key: "totalReviews",
    label: "Total Reviews",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "trust-score",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
    apiField: "numberOfReviews.total",
    description: "Total number of reviews on the business unit.",
  },
  {
    key: "reviewsUsedForTrustScoreCalculation",
    label: "Reviews Used For TrustScore",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "trust-score",
    compatibleWidgets: ["kpi", "data-table"],
    apiField: "numberOfReviews.usedForTrustScoreCalculation",
    description:
      "Subset of reviews that count toward the TrustScore (excludes recused / flagged).",
  },
  {
    key: "numberOfReviewsTotal",
    label: "Reviews (Total Alias)",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "trust-score",
    compatibleWidgets: ["kpi"],
    apiField: "numberOfReviews.total",
    description: "Backwards-compat alias of Total Reviews for legacy widget configs.",
  },

  // === Service Review Aggregations Group (7 — client-side derived) =======
  {
    key: "reviewsInPeriod",
    label: "Reviews in Period",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "reviews",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
    apiField: "reviewsInPeriod",
    description:
      "Reviews filtered by createdAt within the requested dateRange (client-side post-filter — Trustpilot has no server-side date filter).",
  },
  {
    key: "avgStarsInPeriod",
    label: "Average Stars (in Period)",
    type: "number",
    dataType: "Decimal",
    aggregationKind: "ratio", ratioComponents: { denominator: "reviewsInPeriod" },
    category: "reviews",
    compatibleWidgets: ["kpi", "line-chart"],
    apiField: "avgStarsInPeriod",
    description: "Mean of `stars` across the period — NULL when zero reviews.",
  },
  {
    key: "verifiedReviewPercentage",
    label: "Verified Review %",
    type: "percentage",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { denominator: "reviewsInPeriod" },
    category: "reviews",
    compatibleWidgets: ["kpi", "gauge"],
    apiField: "verifiedReviewPercentage",
    description:
      "Percent of reviews with isVerified === true — NULL when zero reviews.",
  },
  {
    key: "companyReplyCount",
    label: "Replied Review Count",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "reply-management",
    compatibleWidgets: ["kpi"],
    apiField: "companyReplyCount",
    description: "Number of reviews where the business replied (companyReply !== null).",
  },
  // Story 23.8 — Trustpilot unreplied review count, derived at aggregation
  // time as `ratedTotal - companyReplyCount`. Powers the Story 23.8
  // Awaiting Response Queue widget's Trustpilot KPI. Mirrors Google
  // Reviews' `unrepliedReviewCount` metric (same key name across both
  // adapters by design — the cross-platform synthetic aggregator joins
  // on the shared key without per-adapter remapping). Flagged
  // `synthetic: true` so the build-time wire-field allowlist check
  // exempts it (the value comes from the aggregator, not a wire-format
  // Trustpilot endpoint field).
  {
    key: "unrepliedReviewCount",
    label: "Unreplied Review Count",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "reply-management",
    compatibleWidgets: ["kpi"],
    apiField: "unrepliedReviewCount",
    synthetic: true,
    description:
      "Number of reviews where the business has NOT replied (companyReply === null). Derived from ratedTotal - companyReplyCount in buildTrustpilotReviewSummary.",
  },
  {
    key: "companyReplyRate",
    label: "Reply Rate",
    type: "percentage",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "companyReplyCount", denominator: "reviewsInPeriod" },
    category: "reply-management",
    compatibleWidgets: ["kpi", "gauge"],
    apiField: "companyReplyRate",
    description: "Replied / total reviews — NULL when zero reviews.",
  },
  {
    key: "avgCompanyReplyLatencySeconds",
    label: "Avg Reply Latency (seconds)",
    type: "duration",
    dataType: "Duration",
    aggregationKind: "ratio", ratioComponents: { denominator: "companyReplyCount" },
    category: "reply-management",
    compatibleWidgets: ["kpi", "line-chart"],
    apiField: "avgCompanyReplyLatencySeconds",
    description:
      "Mean seconds between review createdAt and companyReply.createdAt — NULL when no replies.",
  },
  {
    key: "replyWithin24hPercentage",
    label: "Reply Within 24h %",
    type: "percentage",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { denominator: "companyReplyCount" },
    category: "reply-management",
    compatibleWidgets: ["kpi", "gauge"],
    apiField: "replyWithin24hPercentage",
    description: "Percent of replies issued within 86,400 seconds — NULL when no replies.",
  },

  // === Service Review per-Review Group (8) ================================
  {
    key: "reviewStars",
    label: "Review Stars",
    type: "number",
    dataType: "Integer",
    aggregationKind: "ratio",
    category: "reviews",
    compatibleWidgets: ["data-table"],
    apiField: "stars",
    description: "Per-review 1-5 star rating.",
  },
  {
    key: "reviewIsVerified",
    label: "Review Verified",
    type: "number",
    dataType: "Enum",
    category: "reviews",
    compatibleWidgets: ["data-table", "kpi"],
    apiField: "isVerified",
    description: "Per-review verified-purchase flag.",
  },
  {
    key: "reviewNumberOfLikes",
    label: "Review Likes",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "reviews",
    compatibleWidgets: ["data-table"],
    apiField: "numberOfLikes",
    description: "Number of Trustpilot likes on the review.",
  },
  {
    key: "reviewLanguage",
    label: "Review Language",
    type: "number",
    dataType: "Enum",
    category: "reviews",
    compatibleWidgets: ["data-table"],
    apiField: "language",
    description: "ISO 639-1 language code of the review body.",
  },
  {
    key: "reviewHasReply",
    label: "Has Reply",
    type: "number",
    dataType: "Enum",
    category: "reply-management",
    compatibleWidgets: ["data-table", "kpi"],
    apiField: "hasReply",
    description: "Derived: true when the per-review companyReply !== null.",
  },
  {
    key: "reviewReplyLatencySeconds",
    label: "Review Reply Latency (s)",
    type: "duration",
    dataType: "Duration",
    aggregationKind: "ratio",
    category: "reply-management",
    compatibleWidgets: ["data-table"],
    apiField: "replyLatencySeconds",
    description:
      "Per-review seconds between createdAt and companyReply.createdAt — NULL when no reply.",
  },
  {
    key: "reviewSentimentBucket",
    label: "Sentiment Bucket",
    type: "number",
    dataType: "Enum",
    category: "reviews",
    compatibleWidgets: ["pie-chart", "data-table"],
    apiField: "sentimentBucket",
    description:
      "Derived from stars: 4-5 → positive, 3 → neutral, 1-2 → negative (per AC #4 rule).",
  },
  {
    key: "consumerNumberOfReviews",
    label: "Reviewer Lifetime Reviews",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "reviews",
    compatibleWidgets: ["data-table"],
    apiField: "consumer.numberOfReviews",
    description:
      "Reviewer's lifetime review count on Trustpilot — signal for 'professional reviewer' detection.",
  },
  // === Story 23.4 — Synthetic-aggregate metrics (3) =======================
  // Computed at preset-expansion time. NOT directly fetched from Trustpilot's
  // v1 API; derived from the 30-day reviewsInPeriod window + business-unit-
  // details numberOfReviews split. `apiField: "<derived>"` documents the
  // synthetic source — the metric-allowlist test exempts synthetic-true
  // entries from the per-endpoint wire-format allowlist check.
  // Mirrors Moz 20.8's `domainAuthorityTrend`, CallRail 22.2's
  // `callsTrendDirection`, and GR 23.2's `reviewVelocityPerMonth` precedent.
  {
    key: "reviewVelocityPerMonth",
    label: "Review Velocity (per month)",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "reviews",
    compatibleWidgets: ["kpi"],
    synthetic: true,
    apiField: "<derived>",
    description:
      "Rolling 30-day review count — pace metric. Surfaces 'this DTC brand gets ~N reviews/month' as a single Scorecard KPI.",
  },
  {
    key: "reputationTrendDirection",
    label: "Reputation Trend Direction",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    // Per AC #12 spec snippet — `trust-score` category groups the
    // synthetic trend + eligibility KPIs together in widget-config UI.
    category: "trust-score",
    compatibleWidgets: ["kpi"],
    synthetic: true,
    apiField: "<derived>",
    description:
      "1 = improving (avgStarsInPeriod > trustScore), 0 = flat, -1 = declining. Trend signal for the Cross-Endpoint Scorecard.",
  },
  {
    // Story 23.4 ADR-C — Trustpilot-specific catalogue ADDITION. No analog
    // in Google Reviews / Yelp / GBP — those platforms count every review.
    // Trustpilot's Bayesian scoring drops 5-15% of reviews as ineligible
    // (older / anomalous / unverified). The percentage is a transparency
    // wedge the platform exposes via numberOfReviews.total vs
    // numberOfReviews.usedForTrustScoreCalculation split — Story 23.4's
    // build-to-sell scorecard KPI #4.
    key: "trustScoreEligibleReviewPercentage",
    label: "TrustScore-Eligible Reviews %",
    // AC #12 spec snippet specifies `type: "number"` (the higher-level
    // metric kind); `dataType: "Decimal"` captures the percentage value
    // shape. Don't conflate: `type` is the metric's selection-UI grouping,
    // `dataType` is the wire-shape.
    type: "number",
    dataType: "Decimal",
    aggregationKind: "ratio", ratioComponents: { numerator: "reviewsUsedForTrustScoreCalculation", denominator: "numberOfReviewsTotal" },
    category: "trust-score",
    compatibleWidgets: ["kpi", "gauge"],
    synthetic: true,
    apiField: "<derived>",
    description:
      "usedForTrustScoreCalculation / total * 100 — surfaces the gap between displayed review count and the count actually moving the TrustScore. Trustpilot-specific wedge no competitor review platform exposes (GR / Yelp / GBP count every review).",
  },

  // === Story 35.3 Batch 1 — Per-review private fields (+10) ===============
  // Paid-tier-gated (privateApiEntitled === true) per AC #5 + AC #3.
  // 5 paid-gated + 5 always-available.
  {
    key: "countsTowardsLocationTrustScore",
    label: "Counts Towards Location TrustScore",
    type: "number",
    dataType: "Enum",
    category: "trust-score",
    compatibleWidgets: ["data-table"],
    apiField: "countsTowardsLocationTrustScore",
    description:
      "Story 35.3 Batch 1 — true when this review contributes to the location-level (multi-BU) TrustScore aggregate. Private-tier field per /private/business-units/{id}/reviews. PAID-TIER GATED.",
  },
  {
    key: "findReviewerObject",
    label: "Find Reviewer (private)",
    type: "number",
    dataType: "Enum",
    category: "reviewer-quality",
    compatibleWidgets: ["data-table"],
    apiField: "findReviewer",
    description:
      "Story 35.3 Batch 1 — Trustpilot 'find this reviewer's other reviews' widget metadata: displayName + displayLocation + profileUrl. PII-flagged on displayName per Story 23.3 AC #5 allowlist. PAID-TIER GATED.",
  },
  {
    key: "invitationObject",
    label: "Invitation Link (private)",
    type: "number",
    dataType: "Enum",
    category: "invitations",
    compatibleWidgets: ["data-table"],
    apiField: "invitation",
    description:
      "Story 35.3 Batch 1 — links a review to its originating invitation send: templateName + sentAt. recipientEmail (when present) is PII-redacted on log paths. PAID-TIER GATED.",
  },
  {
    key: "businessUnitHistory",
    label: "Business Unit History (private)",
    type: "number",
    dataType: "Array",
    category: "reviews",
    compatibleWidgets: ["data-table"],
    apiField: "businessUnitHistory",
    description:
      "Story 35.3 Batch 1 — array of historical BU-name objects (e.g., business unit was renamed; original review attribution preserved). CSV-injection stripped on .name. PAID-TIER GATED.",
  },
  {
    key: "locationPrivate",
    label: "Location (private object)",
    type: "number",
    dataType: "Enum",
    category: "reviews",
    compatibleWidgets: ["data-table"],
    apiField: "location",
    description:
      "Story 35.3 Batch 1 — private-tier location object expansion: { id, name, address: { city, country, ... }, displayLocation } per private business-unit endpoint. PAID-TIER GATED.",
  },
  // Always-available (regardless of entitlement)
  {
    key: "consumerId",
    label: "Reviewer ID (consumer)",
    type: "number",
    dataType: "Enum",
    category: "reviewer-quality",
    compatibleWidgets: ["data-table"],
    apiField: "consumer.id",
    description:
      "Story 35.3 Batch 1 — Trustpilot consumer ID surfaced as a top-level key on every review record. Always-available.",
  },
  {
    key: "businessUnitAddressStreet",
    label: "Business Unit Street Address",
    type: "number",
    dataType: "Enum",
    category: "reviews",
    compatibleWidgets: ["data-table"],
    apiField: "address.street",
    description:
      "Story 35.3 Batch 1 — Business Unit street address. CSV-injection stripped on output. Always-available.",
  },
  {
    key: "businessUnitAddressPostcode",
    label: "Business Unit Postcode",
    type: "number",
    dataType: "Enum",
    category: "reviews",
    compatibleWidgets: ["data-table"],
    apiField: "address.postcode",
    description:
      "Story 35.3 Batch 1 — Business Unit postcode. CSV-injection stripped on output. Always-available.",
  },
  {
    key: "wordCount",
    label: "Review Word Count",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "reviews",
    compatibleWidgets: ["data-table", "kpi"],
    apiField: "wordCount",
    description:
      "Story 35.3 Batch 1 — per-review word count of the `text` body computed as `text.trim().split(/\\s+/).filter(Boolean).length`. Always-available derived.",
  },
  {
    key: "consumerLifetimeBand",
    label: "Reviewer Lifetime Band",
    type: "number",
    dataType: "Enum",
    category: "reviewer-quality",
    compatibleWidgets: ["data-table", "pie-chart"],
    apiField: "consumerLifetimeBand",
    description:
      "Story 35.3 Batch 1 — 4-tier bucketed from consumer.numberOfReviews: newcomer (0-2) / established (3-9) / active (10-49) / power_reviewer (50+). Always-available derived.",
  },

  // === Story 35.3 Batch 2 — Invitations API (+8 wire + 3 derived rates) ===
  // NEW category `"invitations"`. Plus-tier-gated via privateApiEntitled
  // flag per AC #3 + 30-day retention pre-flight per AC #4.
  {
    key: "invitationsSent",
    label: "Invitations Sent",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "invitations",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
    apiField: "total_invitations_sent",
    description:
      "Story 35.3 Batch 2 — total invitations dispatched in the date range. Plus-tier gated. 30-day retention.",
  },
  {
    key: "invitationsOpened",
    label: "Invitations Opened",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "invitations",
    compatibleWidgets: ["kpi", "line-chart"],
    apiField: "total_invitations_opened",
    description:
      "Story 35.3 Batch 2 — count opened by recipient (email-open pixel tracked by Trustpilot). Plus-tier gated.",
  },
  {
    key: "invitationsClicked",
    label: "Invitations Clicked",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "invitations",
    compatibleWidgets: ["kpi", "line-chart"],
    apiField: "total_invitations_clicked",
    description:
      "Story 35.3 Batch 2 — count clicked through to the review-write form. Plus-tier gated.",
  },
  {
    key: "invitationsBounced",
    label: "Invitations Bounced",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "invitations",
    compatibleWidgets: ["kpi"],
    apiField: "total_invitations_bounced",
    description:
      "Story 35.3 Batch 2 — count bounced (hard + soft combined; soft bounces NOT broken out). Plus-tier gated.",
  },
  {
    key: "invitationsUnsubscribed",
    label: "Invitations Unsubscribed",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "invitations",
    compatibleWidgets: ["kpi"],
    apiField: "total_invitations_unsubscribed",
    description:
      "Story 35.3 Batch 2 — count where recipient hit the unsubscribe link. Plus-tier gated.",
  },
  {
    key: "invitationsConverted",
    label: "Invitations Converted to Reviews",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "invitations",
    compatibleWidgets: ["kpi", "line-chart"],
    apiField: "total_invitations_converted",
    description:
      "Story 35.3 Batch 2 — count of invitations resulting in a published review. The SINGLE most-valuable funnel metric. Plus-tier gated.",
  },
  {
    key: "templatesActive",
    label: "Active Invitation Templates",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "invitations",
    compatibleWidgets: ["kpi"],
    apiField: "templates_active",
    description:
      "Story 35.3 Batch 2 — derived from templates[].isActive count. Plus-tier gated.",
  },
  {
    key: "invitationLinksActive",
    label: "Active Invitation Links",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "invitations",
    compatibleWidgets: ["kpi"],
    apiField: "invitation_links_active",
    description:
      "Story 35.3 Batch 2 — derived from invitation_links[].isActive count. Plus-tier gated.",
  },
  // 3 synthetic-derived rates — NULL when sent === 0 per AC #6.
  {
    key: "invitationOpenRate",
    label: "Invitation Open Rate %",
    type: "percentage",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "invitationsOpened", denominator: "invitationsSent" },
    category: "invitations",
    compatibleWidgets: ["kpi", "gauge"],
    synthetic: true,
    apiField: "invitation_open_rate",
    description:
      "Story 35.3 Batch 2 derived — opened / sent * 100. NULL when sent=0 (preserves no-data signal). Plus-tier gated.",
  },
  {
    key: "invitationConversionRate",
    label: "Invitation Conversion Rate %",
    type: "percentage",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "invitationsConverted", denominator: "invitationsSent" },
    category: "invitations",
    compatibleWidgets: ["kpi", "gauge"],
    synthetic: true,
    apiField: "invitation_conversion_rate",
    description:
      "Story 35.3 Batch 2 derived — converted / sent * 100. The BUILD-TO-SELL conversion-rate narrative. NULL when sent=0.",
  },
  {
    key: "invitationBounceRate",
    label: "Invitation Bounce Rate %",
    type: "percentage",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "invitationsBounced", denominator: "invitationsSent" },
    category: "invitations",
    compatibleWidgets: ["kpi", "gauge"],
    synthetic: true,
    apiField: "invitation_bounce_rate",
    description:
      "Story 35.3 Batch 2 derived — bounced / sent * 100. Email-list-hygiene signal. NULL when sent=0.",
  },

  // === Story 35.3 Batch 3 — Lifetime aggregates over paginated history (+4) ===
  {
    key: "lifetimeReviewCount",
    label: "Lifetime Review Count",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "trust-score",
    compatibleWidgets: ["kpi"],
    apiField: "lifetimeReviewCount",
    description:
      "Story 35.3 Batch 3 — total reviews fetched across all paginated pages (≤ 600 per the 6-page cap). NULL when caller did NOT opt into pagination.",
  },
  {
    key: "oldestReviewDate",
    label: "Oldest Review Date",
    type: "number",
    dataType: "Enum",
    category: "reviews",
    compatibleWidgets: ["kpi", "data-table"],
    apiField: "oldestReviewDate",
    description:
      "Story 35.3 Batch 3 — min(reviews.createdAt) across the full paginated set. Surfaces 'this brand has been on Trustpilot since X' for build-to-sell longevity narrative.",
  },
  {
    key: "lifetimeAvgStars",
    label: "Lifetime Average Stars",
    type: "number",
    dataType: "Decimal",
    aggregationKind: "ratio", ratioComponents: { denominator: "lifetimeReviewCount" },
    category: "trust-score",
    compatibleWidgets: ["kpi", "gauge"],
    apiField: "lifetimeAvgStars",
    description:
      "Story 35.3 Batch 3 — mean(reviews.stars) across the full paginated set. NULL when zero reviews.",
  },
  {
    key: "lifetimeReplyRate",
    label: "Lifetime Reply Rate %",
    type: "percentage",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { denominator: "lifetimeReviewCount" },
    category: "reply-management",
    compatibleWidgets: ["kpi", "gauge"],
    apiField: "lifetimeReplyRate",
    description:
      "Story 35.3 Batch 3 — count(reviews.companyReply !== null) / lifetimeReviewCount * 100. Lifetime customer-service responsiveness signal. NULL when zero reviews.",
  },
];

export const TRUSTPILOT_DIMENSIONS: ReadonlyArray<DimensionDefinition> = [
  // === Time (2 — always available) ========================================
  { key: "dateCreated", label: "Created At", type: "Time" },
  { key: "dateUpdated", label: "Updated At", type: "Time" },

  // === Review Quality (3 — always available) ==============================
  { key: "starRating", label: "Star Rating", type: "Review", exampleValues: ["1", "2", "3", "4", "5"] },
  { key: "language", label: "Language", type: "Content", exampleValues: ["en", "da", "de"] },
  { key: "verifiedStatus", label: "Verified Status", type: "Review", exampleValues: ["true", "false"] },

  // === Consumer (2 — always available; PII-flagged for log redaction) =====
  {
    key: "consumerId",
    label: "Reviewer ID",
    type: "Review",
    exampleValues: ["consumer-abc123"],
  },
  {
    key: "consumerDisplayLocation",
    label: "Reviewer Location",
    type: "Geo",
    exampleValues: ["US", "London, UK", "Berlin, DE"],
  },

  // === Reply Status (1 — always available) ================================
  { key: "hasCompanyReply", label: "Has Company Reply", type: "Review", exampleValues: ["true", "false"] },

  // === Business Unit (1 — always available) ===============================
  { key: "businessUnit", label: "Business Unit", type: "Structure" },

  // === Private-tier (3 — gated on `privateApiEntitled === true`) ==========
  // Emitted only when the customer has Trustpilot Plus or higher.
  // See `discoverEndpoints` for the runtime gate.
  {
    key: "source",
    label: "Review Source (private)",
    type: "Traffic",
    exampleValues: ["Organic", "InvitationLink", "Api"],
  },
  {
    key: "status",
    label: "Review Status (private)",
    type: "Classification",
    exampleValues: ["active", "reported"],
  },
  {
    key: "tags",
    label: "Review Tags (private)",
    type: "Classification",
  },
];
